import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ReplayGreeting from "@/components/ReplayGreeting";
import { isGridInitialized, speakAs } from "@/lib/audio";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  MessageCircle,
  Send,
  Sparkles,
} from "lucide-react";

const BENTLEY_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/images/c5a753ff30e72a317cf6ca3ee3efc6c9a04d9f4c85f0676f6285bedf16dd7a3b.png";

const BENTLEY_GREETING =
  "Welcome to the river! I'm Bentley. I'm the official mascot of River Bend. I can tell you about the movie tonight, help you find the Bologna Express, or tell you a story about the river. What do you want to do first?";

// Kid-safe reply set — pool, fish, food, movie, campfire. Only fun & amenities.
const REPLIES = [
  {
    match: ["pool", "swim", "water"],
    text: "The pool is open all day! 🏊 The best time to splash is right after lunch — I like to nap on the deck chairs and watch the little kids do cannonballs. Bring your goggles!",
  },
  {
    match: ["fish", "fishing", "creek", "catch"],
    text: "The Olentangy River is full of little sunfish and the occasional smallmouth bass 🐟. Ask a grown-up to take you down by the big oak tree at the bend — that's my favorite spot. Just remember: catch and release, so my river friends can keep swimming!",
  },
  {
    match: ["food", "eat", "hungry", "bologna", "truck", "snack"],
    text: "The Bologna Express Food Truck rolls in Friday at 5 PM! 🚚 They make the best fried bologna sandwiches on the whole river. On Saturday it's the Hog Roast at the pavilion — huge plates, live music, and s'mores after dark.",
  },
  {
    match: ["movie", "tonight", "amphitheater", "watch", "10", "cinema"],
    text: "Tonight's outdoor movie is Top Gun: Maverick at 10 PM! 🎬 Bring a blanket to the amphitheater — the fireflies show up right around 9:45 and it's honestly better than the previews.",
  },
  {
    match: ["campfire", "fire", "smore", "s'more", "marshmallow"],
    text: "Campfires start at 8 PM around the main circle 🔥. Bring sticks — we have marshmallows and chocolate at the camp store. If you sing loud enough, sometimes I sneak over to listen.",
  },
  {
    match: ["story", "legend", "tale", "who are you"],
    text: "Ohhh, that's a good one. Tap the ⭐ 'Legend of Bentley' button below the chat and I'll tell you the whole tale — how a little otter became the guardian of the river.",
  },
  {
    match: ["bug", "mosquito", "bees"],
    text: "The camp store has bug spray if the mosquitos get feisty. My tip: sit close to the campfire smoke — bugs don't like it, and neither do dragons (probably).",
  },
  {
    match: ["playground", "kid", "friend", "play"],
    text: "The big playground is by the pavilion — swings, monkey bars, and a rope bridge that shakes just enough to feel scary. New friends usually meet there right after breakfast.",
  },
];

const DEFAULT_REPLY =
  "Hmm, I don't know that one yet — but ask a grown-up at the camp store and they'll know for sure. I'll be here by the river if you want to try another question!";

// "The Legend of Bentley" — 3 story pages the user asked to reveal.
const LEGEND_PAGES = [
  {
    title: "Page 1 · The Little Otter Who Listened",
    body:
      "Long before there were tents or campfires or fried bologna sandwiches, there was just the river. The Olentangy ran slow and clear past the big oak tree, and in the shallows lived a small otter with bright brown eyes. Nobody told him his name yet, so for a long time he just called himself 'the one who listens.' He listened to the herons and the bullfrogs, to the wind in the sycamores, and to the far-off sound of families laughing over hills he had never crossed.",
  },
  {
    title: "Page 2 · The Night the First Family Came",
    body:
      "One summer evening, a station wagon full of tired parents and three sunburned kids rolled up to the bend in the river. They were lost. The map had lied to them, and the little girl in the back seat was about to cry. That's when the otter — the one who listens — climbed up onto a driftwood log and stared right at their headlights. He led them, slow and steady, down a mossy path to a clearing where the fireflies had already lit the lanterns. The dad whispered, 'Well I'll be. Looks like this fella has a name after all.' And the little girl said, 'Bentley. He looks like a Bentley to me.'",
  },
  {
    title: "Page 3 · The Sentry of the Bend",
    body:
      "That was the first family. Then came more. And more. Until there was a pavilion, and a pool, and a Bologna Express food truck, and outdoor movies under the stars every Friday night. Bentley never left. Every night, when the fires burned low and the kids were finally asleep in their tents, he'd swim up and down the bend, checking the docks, checking the shore, checking the sky. That's why the campers call him the Sentry of the Bend — the little otter who listens, who leads, and who stays. And if you're very quiet at sunrise, sometimes you can still see him on his log, watching over the river.",
  },
];

export default function Bentley() {
  const [conversation, setConversation] = useState([]);
  const [input, setInput] = useState("");
  const [legendOpen, setLegendOpen] = useState(false);
  const [pageIdx, setPageIdx] = useState(0);

  // Fire Bentley's spoken welcome if the session's audio gate has been unlocked.
  useEffect(() => {
    if (isGridInitialized()) {
      const t = setTimeout(() => speakAs("bentley"), 600);
      return () => clearTimeout(t);
    }
  }, []);

  const resolveReply = (question) => {
    const q = question.trim().toLowerCase();
    const hit = REPLIES.find((r) => r.match.some((kw) => q.includes(kw)));
    return hit ? hit.text : DEFAULT_REPLY;
  };

  const send = (raw) => {
    const question = (raw ?? input).trim();
    if (!question) return;
    setConversation((prev) => [
      ...prev,
      { role: "kid", text: question },
      { role: "bentley", text: resolveReply(question) },
    ]);
    setInput("");
  };

  const openLegend = () => {
    setPageIdx(0);
    setLegendOpen(true);
  };

  return (
    <div className="min-h-screen text-white relative overflow-hidden" data-testid="bentley-page">
      <SunriseRiverBackdrop />

      <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pt-6 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/"
            data-testid="bentley-back-home"
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-white/70 hover:text-white transition-colors"
          >
            ← Home
          </Link>
          <div className="flex items-center gap-1.5 text-[9px] sm:text-[10px] uppercase tracking-[0.28em] text-[#7FE7C4]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7FE7C4] light-pulse" />
            River Bend · Live
          </div>
        </div>

        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-14 items-center">
          <BentleyPortrait />

          <div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.28em] mb-5"
              style={{
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.20)",
                color: "#FFE9A8",
                backdropFilter: "blur(6px)",
              }}
              data-testid="bentley-eyebrow"
            >
              <Sparkles size={12} />
              Official Mascot · River Bend
            </div>

            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-white mb-4"
              data-testid="bentley-headline"
              style={{ textShadow: "0 3px 24px rgba(0,0,0,0.55)" }}
            >
              Hi! I&apos;m <span className="italic" style={{ color: "#FFE9A8" }}>Bentley</span>.
            </h1>
            <p
              className="text-base sm:text-lg leading-relaxed max-w-lg"
              style={{ color: "#F1F5F9", textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}
              data-testid="bentley-subheadline"
            >
              Come sit by the river with me. I know all the good spots — the
              pool, the campfire, the food truck, and where the fireflies show
              up at bedtime.
            </p>
            <div className="mt-4">
              <ReplayGreeting character="bentley" label="Replay Bentley" />
            </div>

            <button
              type="button"
              onClick={openLegend}
              data-testid="bentley-legend-button"
              className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-md font-display text-sm uppercase tracking-[0.24em] transition-transform hover:translate-y-[-1px]"
              style={{
                background: "linear-gradient(90deg,#FFC44F,#FF8A3D)",
                color: "#3B1D06",
                boxShadow: "0 14px 34px -14px rgba(255,140,60,0.7)",
              }}
            >
              <BookOpen size={15} /> The Legend of Bentley
            </button>
          </div>
        </div>

        {/* Chat with Bentley */}
        <section
          className="mt-14 rounded-xl border p-5 sm:p-6"
          style={{
            borderColor: "rgba(255,255,255,0.16)",
            background: "rgba(20,28,40,0.55)",
            backdropFilter: "blur(14px)",
            boxShadow: "0 30px 60px -30px rgba(0,0,0,0.6)",
          }}
          data-testid="bentley-chat"
        >
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle size={14} className="text-[#7FE7C4]" />
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/80">
              Chat with Bentley
            </div>
          </div>

          <div
            className="rounded-lg p-4 min-h-[220px] max-h-[420px] overflow-y-auto space-y-3"
            style={{ background: "rgba(0,0,0,0.28)", border: "1px solid rgba(255,255,255,0.08)" }}
            data-testid="bentley-chat-log"
          >
            {/* Greeting always shown first */}
            <ChatBubble side="bentley" name="Bentley">
              {BENTLEY_GREETING}
            </ChatBubble>

            {conversation.map((m, i) => (
              <ChatBubble
                key={i}
                side={m.role === "kid" ? "kid" : "bentley"}
                name={m.role === "kid" ? "You" : "Bentley"}
              >
                {m.text}
              </ChatBubble>
            ))}
          </div>

          {/* Sample chips */}
          {conversation.length === 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="bentley-samples">
              {["Pool?", "What's for dinner?", "Movie tonight?", "Any fish?", "Campfire time?"].map(
                (q, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => send(q)}
                    data-testid={`bentley-sample-${i}`}
                    className="px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.2em] transition-colors"
                    style={{
                      background: "rgba(255,228,153,0.15)",
                      border: "1px solid rgba(255,228,153,0.45)",
                      color: "#FFE9A8",
                    }}
                  >
                    {q}
                  </button>
                ),
              )}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="mt-4 flex items-center gap-2"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Bentley something…"
              data-testid="bentley-input"
              className="flex-1 rounded-md px-4 py-3 text-base text-white focus:outline-none"
              style={{
                background: "rgba(0,0,0,0.4)",
                border: "1px solid rgba(255,255,255,0.14)",
              }}
              maxLength={120}
            />
            <button
              type="submit"
              data-testid="bentley-send"
              aria-label="Send"
              className="h-11 w-11 rounded-md inline-flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg,#7FE7C4,#3AC0A0)",
                color: "#0A0A0F",
              }}
            >
              <Send size={15} />
            </button>
          </form>
        </section>
      </div>

      {legendOpen && (
        <LegendModal
          pageIdx={pageIdx}
          setPageIdx={setPageIdx}
          onClose={() => setLegendOpen(false)}
        />
      )}
    </div>
  );
}

/* ----------------------------------------------------------- Chat Bubble --- */

function ChatBubble({ side, name, children }) {
  const isBentley = side === "bentley";
  return (
    <div className={`flex ${isBentley ? "" : "flex-row-reverse"} items-start gap-2`}>
      <div
        className="w-7 h-7 shrink-0 rounded-full inline-flex items-center justify-center text-[11px] font-display"
        style={{
          background: isBentley ? "rgba(127,231,196,0.18)" : "rgba(255,228,153,0.18)",
          border: `1px solid ${isBentley ? "#7FE7C4" : "#FFE9A8"}`,
          color: isBentley ? "#7FE7C4" : "#FFE9A8",
        }}
      >
        {isBentley ? "B" : "🙂"}
      </div>
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-[13px] leading-relaxed ${
          isBentley ? "" : "text-right"
        }`}
        style={{
          background: isBentley ? "rgba(127,231,196,0.10)" : "rgba(255,228,153,0.10)",
          border: `1px solid ${isBentley ? "rgba(127,231,196,0.30)" : "rgba(255,228,153,0.30)"}`,
          color: "#F8FAFC",
        }}
      >
        <div
          className="text-[9px] uppercase tracking-[0.28em] mb-1"
          style={{ color: isBentley ? "#7FE7C4" : "#FFE9A8" }}
        >
          {name}
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------- Sunrise BG --- */

function SunriseRiverBackdrop() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* Sky gradient */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, #2A1638 0%, #6B2A45 22%, #C0663A 48%, #E89A4A 62%, #7A9E7E 82%, #2C4B3E 100%)",
        }}
      />
      {/* Sun */}
      <div
        className="absolute"
        style={{
          left: "62%",
          top: "36%",
          width: 160,
          height: 160,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 40% 40%, #FFF3C4 0%, #FFC77A 45%, rgba(255,148,84,0) 72%)",
          filter: "blur(2px)",
        }}
      />
      {/* Sun glow */}
      <div
        className="absolute"
        style={{
          left: "40%",
          top: "20%",
          width: 700,
          height: 700,
          background:
            "radial-gradient(circle, rgba(255,190,110,0.45) 0%, rgba(255,190,110,0) 60%)",
        }}
      />
      {/* Distant treeline */}
      <svg
        className="absolute left-0 right-0"
        style={{ top: "58%" }}
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        width="100%"
        height="120"
      >
        <path
          d="M0 90 L40 55 L80 78 L120 40 L160 70 L200 30 L240 62 L280 45 L320 85 L360 50 L400 68 L440 30 L480 60 L520 40 L560 78 L600 45 L640 70 L680 25 L720 62 L760 42 L800 80 L840 48 L880 68 L920 32 L960 58 L1000 42 L1040 82 L1080 50 L1120 66 L1160 30 L1200 60 L1240 42 L1280 78 L1320 48 L1360 66 L1400 32 L1440 60 L1440 120 L0 120 Z"
          fill="#1F3A2C"
          opacity="0.85"
        />
      </svg>
      {/* Water reflection band */}
      <div
        className="absolute left-0 right-0"
        style={{
          top: "62%",
          height: "38%",
          background:
            "linear-gradient(180deg, rgba(255,180,90,0.25) 0%, rgba(60,90,80,0.55) 30%, rgba(15,32,28,0.9) 100%)",
        }}
      />
      {/* Water ripples */}
      <svg
        className="absolute left-0 right-0"
        style={{ top: "72%" }}
        viewBox="0 0 1440 80"
        preserveAspectRatio="none"
        width="100%"
        height="80"
      >
        {[6, 22, 38, 54, 70].map((y, i) => (
          <path
            key={i}
            d={`M0 ${y} Q 360 ${y - 4} 720 ${y} T 1440 ${y}`}
            stroke="#FFDAA8"
            strokeWidth="1"
            fill="none"
            opacity={0.25 - i * 0.04}
          />
        ))}
      </svg>
      {/* Grain / haze */}
      <div
        className="absolute inset-0 mix-blend-overlay"
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(0,0,0,0.35) 100%)",
        }}
      />
    </div>
  );
}

/* -------------------------------------------------------- Portrait card --- */

function BentleyPortrait() {
  return (
    <div className="relative mx-auto lg:mx-0 w-full max-w-[380px]" data-testid="bentley-portrait">
      <div
        className="relative rounded-2xl overflow-hidden"
        style={{
          aspectRatio: "1 / 1",
          border: "1px solid rgba(255,228,168,0.45)",
          boxShadow:
            "0 30px 80px -30px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,228,168,0.10) inset",
        }}
      >
        <img
          src={BENTLEY_IMAGE}
          alt="Bentley the Otter — official mascot of River Bend"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="bentley-image"
          loading="eager"
        />
        {/* Glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 50% 25%, rgba(255,220,150,0.30) 0%, rgba(255,220,150,0) 55%)",
          }}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------- Legend modal --- */

function LegendModal({ pageIdx, setPageIdx, onClose }) {
  const page = LEGEND_PAGES[pageIdx];
  const hasPrev = pageIdx > 0;
  const hasNext = pageIdx < LEGEND_PAGES.length - 1;

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && pageIdx < LEGEND_PAGES.length - 1) setPageIdx((i) => i + 1);
      if (e.key === "ArrowLeft" && pageIdx > 0) setPageIdx((i) => i - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pageIdx, setPageIdx, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(6,11,20,0.78)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="bentley-legend-modal"
      role="dialog"
      aria-label="The Legend of Bentley"
    >
      <div
        className="relative w-full max-w-lg rounded-xl p-6 sm:p-8"
        style={{
          background: "linear-gradient(180deg,#3B1D06 0%,#1B0F04 100%)",
          border: "1px solid rgba(255,196,79,0.45)",
          boxShadow: "0 30px 60px -20px rgba(0,0,0,0.8)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div
            className="text-[10px] uppercase tracking-[0.32em]"
            style={{ color: "#FFC44F" }}
          >
            The Legend of Bentley
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="bentley-legend-close"
            className="text-[10px] uppercase tracking-[0.28em] text-white/60 hover:text-white transition-colors"
          >
            Close ✕
          </button>
        </div>

        <div
          className="font-display text-xl sm:text-2xl tracking-tight text-white mb-4"
          data-testid={`bentley-legend-title-${pageIdx}`}
        >
          {page.title}
        </div>

        <p
          className="text-[15px] leading-relaxed text-[#F5E6C8]"
          data-testid={`bentley-legend-body-${pageIdx}`}
        >
          {page.body}
        </p>

        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={() => hasPrev && setPageIdx(pageIdx - 1)}
            disabled={!hasPrev}
            data-testid="bentley-legend-prev"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.24em] transition-colors"
            style={{
              color: hasPrev ? "#FFE9A8" : "rgba(255,233,168,0.25)",
              cursor: hasPrev ? "pointer" : "not-allowed",
            }}
          >
            <ArrowLeft size={12} /> Prev
          </button>

          <div className="flex items-center gap-2">
            {LEGEND_PAGES.map((_, i) => (
              <span
                key={i}
                className="w-2 h-2 rounded-full transition-colors"
                style={{
                  background:
                    i === pageIdx ? "#FFC44F" : "rgba(255,196,79,0.25)",
                }}
                data-testid={`bentley-legend-dot-${i}`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => hasNext && setPageIdx(pageIdx + 1)}
            disabled={!hasNext}
            data-testid="bentley-legend-next"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.24em] transition-colors"
            style={{
              color: hasNext ? "#FFE9A8" : "rgba(255,233,168,0.25)",
              cursor: hasNext ? "pointer" : "not-allowed",
            }}
          >
            Next <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
